import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, X } from "lucide-react";
import { GenieLoader } from "@/components/ui/GenieLoader";
import { suiteApps, suiteLandingPath } from "@/components/layout/navConfig";
import { useSuiteHandoff, hasSeenExplainer } from "./useSuiteHandoff";
import { FeatureList, GlowField, tint } from "./primitives";
import type { ExplainerDeck, SuiteAppWiring } from "./types";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const BRAND_GRADIENT = "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)";

interface SuiteExplainerProps {
  /** Suite app id (e.g. "mail") — resolves wiring from navConfig. */
  appId: string;
  /** The product story + immersive visuals. */
  deck: ExplainerDeck;
}

/** Resolve wiring (product word, colour, URLs) for an app from navConfig. */
const resolveWiring = (appId: string): SuiteAppWiring | null => {
  const app = suiteApps.find((a) => a.productWord.toLowerCase() === appId.toLowerCase());
  if (!app?.externalUrl) return null;
  return {
    appId,
    productWord: app.productWord,
    color: app.color,
    appUrl: app.externalUrl,
    ssoEntryPath: app.ssoEntryPath,
    genieIcon: app.genieIcon,
  };
};

const SuiteExplainer = ({ appId, deck }: SuiteExplainerProps) => {
  const wiring = useMemo(() => resolveWiring(appId), [appId]);
  const navigate = useNavigate();

  if (!wiring) {
    return <SuiteExplainerFallback onGo={() => navigate(suiteLandingPath, { replace: true })} />;
  }
  return <SuiteExplainerInner wiring={wiring} deck={deck} />;
};

const SuiteExplainerInner = ({ wiring, deck }: { wiring: SuiteAppWiring; deck: ExplainerDeck }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { ready, entering, enterApp } = useSuiteHandoff(wiring);
  const [step, setStep] = useState(0);
  const fastPathStarted = useRef(false);

  const { productWord, genieIcon } = wiring;
  // Unified brand accent for all chrome (eyebrow, progress, glow, feature tints)
  // so every deck reads unmistakably as Help Genie rather than taking each app's
  // switcher colour. The signature blue→pink gradient still carries headlines/CTAs.
  const color = "#4E9CFF";
  const slides = deck.slides;
  const total = slides.length;
  const current = slides[step];
  const isLast = step === total - 1;
  const enterLabel = deck.enterLabel ?? `Enter Help Genie ${productWord}`;
  const forceIntro = searchParams.get("intro") === "1";
  const skipping = !forceIntro && hasSeenExplainer(wiring.appId);

  // Back to the page the user came from; the Suite directory when this route
  // was the first entry in the tab's history (direct link / fresh tab).
  const leave = useCallback(() => {
    if (location.key !== "default") navigate(-1);
    else navigate(suiteLandingPath, { replace: true });
  }, [location.key, navigate]);

  // The satellite opens in a new tab, so this tab returns to where it was.
  const handoff = useCallback(
    async (source: string) => {
      if (await enterApp(source)) leave();
    },
    [enterApp, leave],
  );

  // Fast-path: returning visitors skip straight through (SSO still warms up).
  useEffect(() => {
    if (fastPathStarted.current) return;
    if (skipping) {
      fastPathStarted.current = true;
      void handoff("explainer-fastpath");
    }
  }, [skipping, handoff]);

  const next = useCallback(() => {
    setStep((s) => {
      if (s >= total - 1) {
        void handoff("explainer");
        return s;
      }
      return s + 1;
    });
  }, [total, handoff]);
  const back = useCallback(() => setStep((s) => Math.max(s - 1, 0)), []);

  // Keyboard: ← → to move, Enter to advance/enter.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "Enter") next();
      else if (e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, back]);

  // Fast-path / final handoff → full-screen loader while the tab swaps.
  if (skipping || (entering && isLast)) {
    return <GenieLoader size="fullscreen" loadingText={`Opening Help Genie ${productWord}…`} />;
  }

  return (
    <div className="relative flex min-h-[100dvh] w-full flex-col overflow-hidden bg-[#F9FAFC]">
      <GlowField accent={color} />

      {/* Top chrome */}
      <header className="relative z-10 flex items-center justify-between px-5 py-4 sm:px-10 sm:py-6">
        <div className="flex items-center gap-2.5">
          <span
            className="flex h-9 w-9 items-center justify-center rounded-xl"
            style={{ backgroundColor: tint(color, 0.14) }}
          >
            <GenieIcon name={genieIcon} size={20} />
          </span>
          <span className="text-sm font-bold text-[#222B45] sm:text-base">
            Help Genie{" "}
            <span
              className="bg-clip-text italic text-transparent"
              style={{ backgroundImage: BRAND_GRADIENT }}
            >
              {productWord}
            </span>
          </span>
        </div>

        <div className="flex items-center gap-3 sm:gap-5">
          <button
            type="button"
            onClick={() => void handoff("explainer-skip")}
            className="text-sm font-semibold text-genie-textSecondary transition-colors hover:text-genie-primary"
          >
            Skip intro and open {productWord}
          </button>
          <button
            type="button"
            aria-label="Exit"
            onClick={leave}
            className="flex h-8 w-8 items-center justify-center rounded-full text-genie-textSecondary transition-colors hover:bg-black/5 hover:text-[#222B45]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Slide body */}
      <main className="relative z-10 mx-auto flex w-full max-w-6xl flex-1 items-center px-5 pb-4 sm:px-10">
        <div
          key={current.key}
          className="grid w-full grid-cols-1 items-center gap-8 animate-in fade-in duration-500 lg:grid-cols-2 lg:gap-14"
        >
          {/* Copy */}
          <div className="order-2 lg:order-1 animate-in fade-in slide-in-from-bottom-3 duration-500">
            <span
              className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider"
              style={{ backgroundColor: tint(color, 0.12), color: color }}
            >
              {current.eyebrow}
            </span>
            <h1 className="mt-4 text-3xl font-extrabold leading-[1.08] tracking-tight text-[#222B45] sm:text-4xl lg:text-5xl">
              {current.title}
            </h1>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-[#64748B] sm:text-lg">
              {current.body}
            </p>
            {current.features && current.features.length > 0 && (
              <FeatureList features={current.features} accent={color} />
            )}
          </div>

          {/* Visual */}
          <div
            className="order-1 lg:order-2 animate-in fade-in zoom-in-95 duration-500"
            style={{ animationDelay: "80ms", animationFillMode: "both" } as CSSProperties}
          >
            {current.visual}
          </div>
        </div>
      </main>

      {/* Bottom nav */}
      <footer className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-5 py-5 sm:px-10 sm:py-7">
        <button
          type="button"
          onClick={back}
          disabled={step === 0}
          className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-genie-textSecondary transition-colors hover:bg-black/5 disabled:pointer-events-none disabled:opacity-0"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        {/* Progress segments */}
        <div className="flex items-center gap-2">
          {slides.map((s, i) => (
            <button
              key={s.key}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setStep(i)}
              className="h-1.5 rounded-full transition-all duration-300"
              style={{
                width: i === step ? 28 : 10,
                backgroundColor: i === step ? color : tint(color, 0.25),
              }}
            />
          ))}
        </div>

        <div className="flex items-center gap-3">
          <SsoStatus ready={ready} />
          <button
            type="button"
            onClick={next}
            className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-genie-primary/30 transition-all duration-200 hover:opacity-95 hover:shadow-xl sm:px-6"
            style={{ background: BRAND_GRADIENT }}
          >
            {isLast ? enterLabel : "Next"}
            {entering ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          </button>
        </div>
      </footer>
    </div>
  );
};

/** "Signing you in" affordance so the background SSO warm-up is visible. */
const SsoStatus = ({ ready }: { ready: boolean }) => (
  <span className="hidden items-center gap-1.5 text-xs text-genie-textSecondary sm:inline-flex">
    {ready ? (
      <>
        <Check className="h-3.5 w-3.5 text-green-500" />
        Ready to go
      </>
    ) : (
      <>
        <Loader2 className="h-3.5 w-3.5 animate-spin text-genie-primary" />
        Signing you in…
      </>
    )}
  </span>
);

const SuiteExplainerFallback = ({ onGo }: { onGo: () => void }) => {
  useEffect(() => {
    onGo();
  }, [onGo]);
  return <GenieLoader size="fullscreen" loadingText="Opening the Suite…" />;
};

export default SuiteExplainer;
